import React, { useState, useEffect } from 'react';
import { getAllUsers, approveUser, rejectUser, setAdminStatus } from '../services/users';
import SectionHelpButton from '../components/SectionHelpButton';
import EmptyState from '../components/EmptyState';
import { trackUserApproved, trackUserRoleChanged } from '../services/analytics';
import './GebruikersPage.css';

/**
 * Gebruikers Page (Admin Panel)
 * Shows all users with their status and admin actions
 */
const GebruikersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which user action is loading

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const allUsers = await getAllUsers();
      // Sort: pending first, then approved, then rejected
      const sortedUsers = allUsers.sort((a, b) => {
        const order = { 'pending': 0, 'approved': 1, 'rejected': 2 };
        const aOrder = order[a.status] ?? 1;
        const bOrder = order[b.status] ?? 1;
        return aOrder - bOrder;
      });
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Fout bij het laden van gebruikers');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (uid) => {
    setActionLoading(uid);
    try {
      await approveUser(uid);
      trackUserApproved();
      await loadUsers();
    } catch (err) {
      console.error('Error approving user:', err);
      alert('Fout bij het goedkeuren van gebruiker');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (uid) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker wilt afwijzen?')) return;
    setActionLoading(uid);
    try {
      await rejectUser(uid);
      await loadUsers();
    } catch (err) {
      console.error('Error rejecting user:', err);
      alert('Fout bij het afwijzen van gebruiker');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (uid, currentIsAdmin) => {
    const action = currentIsAdmin ? 'verwijderen' : 'toekennen';
    if (!window.confirm(`Weet je zeker dat je admin rechten wilt ${action}?`)) return;
    setActionLoading(uid);
    try {
      await setAdminStatus(uid, !currentIsAdmin);
      trackUserRoleChanged();
      await loadUsers();
    } catch (err) {
      console.error('Error toggling admin:', err);
      alert('Fout bij het wijzigen van admin status');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const pendingCount = users.filter(u => u.status === 'pending').length;

  const getDisplayName = (user) => {
    const fullName = `${user.voornaam || ''} ${user.achternaam || ''}`.trim();
    return fullName || user.email;
  };

  if (loading) {
    return (
      <div className="gebruikers-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gebruikers-page">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="gebruikers-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Gebruikers van de app</h2>
            <SectionHelpButton
              title="Gebruikers"
              purpose="Deze sectie helpt admins om toegang tot de app te controleren en rechten te beheren."
              instructions={[
                'Controleer nieuwe aanvragen met status Wachtend en keur ze goed of wijs ze af.',
                'Gebruik admin-acties bij goedgekeurde gebruikers om rechten toe te kennen of in te trekken.',
                'Gebruik de statuskolom om snel de huidige toegangssituatie van elke gebruiker te zien.'
              ]}
            />
          </div>
          <p className="app-page-description">Controleer aanvragen en beheer goedkeuringen en adminrechten.</p>
        </div>
        {pendingCount > 0 && (
          <span className="pending-count">{pendingCount} wachtend op goedkeuring</span>
        )}
      </div>

      {users.length === 0 ? (
        <EmptyState icon="👥" message="Geen gebruikers gevonden" />
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Naam</th>
                <th>Email</th>
                <th>Status</th>
                <th>Admin</th>
                <th>Aangemeld</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className={user.status === 'pending' ? 'pending-row' : ''}>
                  <td className="name-cell" data-label="Naam">{getDisplayName(user)}</td>
                  <td className="email-cell" data-label="Email">{user.email}</td>
                  <td className="status-cell" data-label="Status">
                    {user.status === 'rejected' && <span className="status rejected">Afgewezen</span>}
                    {user.status === 'pending' && <span className="status pending">Wachtend</span>}
                    {user.status === 'approved' && <span className="status approved">Goedgekeurd</span>}
                  </td>
                  <td className="admin-cell" data-label="Admin">
                    {user.isAdmin ? <span className="admin-yes">Ja</span> : <span className="admin-no">Nee</span>}
                  </td>
                  <td className="date-cell" data-label="Aangemeld">{formatDate(user.createdAt)}</td>
                  <td className="actions-cell">
                    {actionLoading === user.id ? (
                      <span className="action-loading">Laden...</span>
                    ) : (
                      <>
                        {user.status === 'pending' && (
                          <>
                            <button 
                              className="action-btn approve"
                              onClick={() => handleApprove(user.id)}
                              title="Goedkeuren"
                            >
                              ✓
                            </button>
                            <button 
                              className="action-btn reject"
                              onClick={() => handleReject(user.id)}
                              title="Afwijzen"
                            >
                              ✗
                            </button>
                          </>
                        )}
                        {user.status === 'approved' && (
                          <button 
                            className={`action-btn text ${user.isAdmin ? 'remove-admin' : 'make-admin'}`}
                            onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                          >
                            {user.isAdmin ? 'Verwijder admin' : 'Maak admin'}
                          </button>
                        )}
                        {user.status === 'rejected' && (
                          <button 
                            className="action-btn approve"
                            onClick={() => handleApprove(user.id)}
                            title="Alsnog goedkeuren"
                          >
                            ✓
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GebruikersPage;
